/* Basic KPI/funnel events forwarded to PostHog. No-ops until the consent
   gate has actually loaded PostHog (window.posthog only exists post-consent,
   see heimat-consent-gate.js), so these never fire ahead of consent. */
(function () {
  'use strict';

  var ADD_TO_CART_SOURCES = ['product-form', 'quick-add', 'cart-upsell', 'free-gift'];

  function currencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null;
  }

  // Hand the PostHog distinct ID to checkout via a cart attribute, so the Web
  // Pixel on checkout can stitch checkout_completed back to this visitor.
  //
  // Written whenever the cart changes rather than when the checkout button is
  // clicked. The express checkout buttons rendered by
  // content_for_additional_checkout_buttons (main-cart-footer.liquid) never fire
  // the cart form's submit event, so a submit listener silently misses those
  // orders entirely. Listening for the submit also put analytics on the critical
  // path to checkout, which is where the dropped-submitter bug came from. Nothing
  // here touches the checkout button.
  var SYNC_SESSION_KEY = 'heimat-posthog-cart-synced';
  var syncInFlight = false;

  function rememberSynced(distinctId) {
    try {
      window.sessionStorage.setItem(SYNC_SESSION_KEY, distinctId);
    } catch (e) {}
  }

  function alreadySyncedThisSession(distinctId) {
    try {
      return window.sessionStorage.getItem(SYNC_SESSION_KEY) === distinctId;
    } catch (e) {
      return false;
    }
  }

  function syncDistinctIdToCart() {
    if (syncInFlight) return;
    // get_distinct_id is missing from PostHog's pre-load stub, so it only exists
    // once array.js has actually landed.
    if (!window.posthog || typeof window.posthog.get_distinct_id !== 'function') return;
    if (!window.routes || !window.routes.cart_update_url) return;

    var distinctId = window.posthog.get_distinct_id();
    if (!distinctId) return;

    syncInFlight = true;
    fetch('/cart.js')
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        // Shopify drops attributes on an empty cart, so there is nothing to
        // attach to until it has contents.
        if (!cart.item_count) return null;
        if (cart.attributes && cart.attributes.posthog_distinct_id === distinctId) {
          rememberSynced(distinctId);
          return null;
        }
        // /cart/update.js replaces the entire attributes object rather than
        // merging keys, so writing our key blind would silently wipe out any
        // attribute another app or theme feature already set on this cart.
        // Read the current attributes first and merge ours in.
        var attributes = Object.assign({}, cart.attributes, {
          posthog_distinct_id: distinctId
        });
        return fetch(window.routes.cart_update_url, {
          method: 'POST',
          // Accept keeps Shopify on the JSON response; without it /cart/update
          // answers with a redirect to the rendered cart page.
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes })
        }).then(function (response) {
          // Only remember a write that actually landed, otherwise the session
          // guard would suppress the retry on every later page view.
          if (response.ok) rememberSynced(distinctId);
        });
      })
      .then(function () { syncInFlight = false; })
      .catch(function () { syncInFlight = false; });
  }

  // PostHog only exists once the consent gate activates it, which can land after
  // DOMContentLoaded. Retry briefly so a returning visitor who heads straight to
  // checkout without touching the cart still gets the attribute written. The
  // session guard keeps this to one /cart.js read per visit rather than one per
  // page view; cart changes re-sync through the cartUpdate subscriber regardless.
  var loadSyncAttempts = 0;
  function syncOnLoad() {
    if (window.posthog && typeof window.posthog.get_distinct_id === 'function') {
      if (!alreadySyncedThisSession(window.posthog.get_distinct_id())) syncDistinctIdToCart();
      return;
    }
    if (++loadSyncAttempts >= 10) return;
    setTimeout(syncOnLoad, 500);
  }
  syncOnLoad();

  // cartUpdate fires from several places (cart page changes, quick order list
  // qty edits) that aren't actually "item added" — only capture the sources
  // that come right after a cart/add.js call.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      // Any cart change is a chance to write the checkout handoff attribute,
      // including quantity edits that are not adds.
      syncDistinctIdToCart();

      if (!window.posthog) return;
      if (ADD_TO_CART_SOURCES.indexOf(event.source) === -1) return;

      var cartData = event.cartData;
      if (!cartData) return;

      function findByVariantId(id) {
        if (!cartData.items) return null;
        for (var i = 0; i < cartData.items.length; i++) {
          if (String(cartData.items[i].variant_id) === String(id)) return cartData.items[i];
        }
        return null;
      }

      // cartTotal is the cart's total_price in cents, or null when unknown.
      // addedQuantity is how many units this add actually put in the cart, or
      // null when the publisher could not tell us.
      function captureItem(item, cartTotal, addedQuantity) {
        if (!item) return;

        // Shopify merges an add into an existing line, so item.quantity and
        // final_line_price describe the whole line, not this add. Price the
        // event on the units that were actually added; fall back to the line
        // when the publisher did not report a quantity.
        var unitPrice = item.final_line_price != null && item.quantity
          ? item.final_line_price / item.quantity
          : (item.price || 0);
        var quantity = addedQuantity != null ? addedQuantity : (item.quantity || 0);

        window.posthog.capture('add_to_cart', {
          product_id: item.product_id,
          variant_id: item.variant_id,
          product_title: item.product_title,
          price: (item.price || 0) / 100,
          quantity: quantity,
          value: (unitPrice * quantity) / 100,
          currency: currencyCode(),
          cart_total: cartTotal != null ? cartTotal / 100 : null,
          source: event.source
        });
      }

      // Present on the sources that hand us a whole cart object; absent on the
      // product-form response, which is handled separately below.
      var cartTotal = cartData.total_price != null ? cartData.total_price : null;

      if (event.variantIds && event.variantIds.length) {
        // Multi-item adds (quick-add-bulk, free-gift) — one event per matched
        // item. Shopify merges an add into an existing same-variant line rather
        // than appending it, so a naive "last item" guess would silently
        // mislabel the event with an unrelated product; only capture what we
        // can actually match by variant id.
        for (var i = 0; i < event.variantIds.length; i++) {
          var id = event.variantIds[i];
          var added = event.addedQuantities ? event.addedQuantities[id] : null;
          // quick-add-bulk posts absolute quantities, so the same event covers
          // decreases and removals. Those are not adds — skip anything that did
          // not raise the line.
          if (event.addedQuantities && !(added > 0)) continue;
          captureItem(findByVariantId(id), cartTotal, added > 0 ? added : null);
        }
        return;
      }

      var variantId = event.productVariantId || event.variantId;
      var addedQuantity = event.addedQuantity != null ? event.addedQuantity : null;
      if (cartData.items && cartData.items.length) {
        // Full cart object (cart-upsell fetches /cart.js, which returns the
        // whole cart) — match the exact line, same reasoning as above.
        captureItem(variantId ? findByVariantId(variantId) : null, cartTotal, addedQuantity);
      } else if (cartData.variant_id) {
        // /cart/add.js posted as FormData (the main product-form "Add to cart"
        // button) returns the added line item itself, not { items: [...] }, so
        // it carries no cart total. That is the most common add path, so
        // reading total_price off it left cart_total null on nearly every
        // event. Fetch the cart instead, so cart_total means the same thing on
        // every source. The add has already succeeded; this only delays the
        // analytics call, never the UI.
        fetch('/cart.js')
          .then(function (response) { return response.json(); })
          .then(function (cart) { captureItem(cartData, cart.total_price, addedQuantity); })
          .catch(function () { captureItem(cartData, null, addedQuantity); });
      }
    });
  }

  // The header renders two search UIs from assets/heimat-search.js (via
  // sections/header-heimat.liquid), not Dawn's <details> search modal, so there
  // is no toggle event to listen for: an inline bar sitting in the header, and
  // an overlay behind the icon button. Both mark themselves with
  // data-open="true" on their root element, so watching that attribute covers
  // every open path (icon click, input focus, Cmd/Ctrl+K) and survives
  // heimat-search.js re-parenting the overlay to <body>.
  var SEARCH_ROOTS = [
    { selector: '[data-heimat-search]', variant: 'overlay' },
    { selector: '[data-heimat-inline-search]', variant: 'inline' }
  ];

  function watchSearch() {
    for (var i = 0; i < SEARCH_ROOTS.length; i++) {
      (function (root) {
        var el = document.querySelector(root.selector);
        if (!el) return;
        var isOpen = el.getAttribute('data-open') === 'true';
        new MutationObserver(function () {
          var nextOpen = el.getAttribute('data-open') === 'true';
          if (nextOpen && !isOpen && window.posthog) {
            window.posthog.capture('search_opened', { variant: root.variant });
          }
          isOpen = nextOpen;
        }).observe(el, { attributes: true, attributeFilter: ['data-open'] });
      })(SEARCH_ROOTS[i]);
    }
  }

  if (document.body) watchSearch();
  else document.addEventListener('DOMContentLoaded', watchSearch);

  // Query actually typed, and clicks on a result — the engagement metrics for the
  // mobile search bar test. Both UIs report through DOM events from
  // heimat-search.js; the term is the settled one, after debouncing.
  // Someone typing "popcorn" slowly triggers a fetch on "pop" and again on
  // "popcorn", and counting both would inflate searches-per-session on whichever
  // variant makes people type more. Hold the event back and let a later query on
  // the same UI replace it, so only the query they settled on gets counted.
  var SEARCH_SETTLE_MS = 600;
  var pendingSearch = null;
  var pendingSearchTimer = null;

  function flushSearch() {
    clearTimeout(pendingSearchTimer);
    pendingSearchTimer = null;
    if (!pendingSearch) return;
    var detail = pendingSearch;
    pendingSearch = null;
    if (!window.posthog) return;
    window.posthog.capture('search_performed', {
      variant: detail.variant,
      query: detail.term,
      results: detail.results
    });
  }

  document.addEventListener('heimat:search:performed', function (event) {
    if (!event.detail) return;
    pendingSearch = event.detail;
    clearTimeout(pendingSearchTimer);
    pendingSearchTimer = setTimeout(flushSearch, SEARCH_SETTLE_MS);
  });

  document.addEventListener('heimat:search:result-click', function (event) {
    if (!event.detail) return;
    // The click navigates away, so send the query it belongs to first.
    flushSearch();
    if (!window.posthog) return;
    window.posthog.capture('search_result_click', {
      variant: event.detail.variant,
      query: event.detail.term,
      url: event.detail.url
    });
  });

  // A query typed right before leaving would otherwise die in the timer.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushSearch();
  });

  // Which theme served this pageview. Kept from the search bar rollout because it is
  // useful on its own: it says which theme an event came from, without which any
  // future theme comparison mixes them together.
  //
  // The unregister calls clear what the rollout left behind. posthog.register writes
  // super properties into the visitor's browser and nothing clears them on its own,
  // so without this a returning visitor would keep stamping search_bar_variant and
  // $feature/bigger-search-bar on every event long after the test ended. Safe to
  // delete once returning visitors have cycled through, say after a few months.
  var themeAttempts = 0;
  var themeRetryTimer = null;

  function registerTheme() {
    if (themeRetryTimer) {
      clearTimeout(themeRetryTimer);
      themeRetryTimer = null;
    }

    if (!window.posthog || typeof window.posthog.register !== 'function') {
      if (++themeAttempts >= 10) return;
      themeRetryTimer = setTimeout(registerTheme, 500);
      return;
    }

    var theme = (window.Shopify && window.Shopify.theme) || {};
    window.posthog.register({
      shopify_theme_id: theme.id ? String(theme.id) : null,
      shopify_theme_name: theme.name || null
    });

    if (typeof window.posthog.unregister === 'function') {
      window.posthog.unregister('search_bar_variant');
      window.posthog.unregister('$feature/bigger-search-bar');
    }
  }
  registerTheme();

  // The retries above cover roughly five seconds, which only spans the case where
  // consent is already stored. A visitor who accepts the banner later gets PostHog
  // long after those gave up, so start the loop over when consent lands.
  document.addEventListener('visitorConsentCollected', function () {
    themeAttempts = 0;
    registerTheme();
  });

})();
